import React, {
  useContext, useCallback, useRef, useEffect,
} from 'react';
import { OfferContext, OfferActionTypes } from '../../../stores/contexts';
import { useOffer, useScrollLoad } from '../../../stores/hooks';
import { OfferParamsType } from '../../../services/httpRequest';
import { OfferStatusTypes } from '../../../constants';
import { AppElement, AppFC } from '../../../interfaces';

interface ScrollLoadOffersProps {
  children: React.ReactNode;
  filterParams?: OfferParamsType;
  /**
   * Optional intersection ratio (0..1) for using IntersectionObserver.
   * When provided, a sentinel element is observed and `onLoadMore` triggers
   * when the sentinel intersects the viewport at the given ratio.
   * Example: 0.5 triggers when the sentinel reaches the center of the viewport.
   */
  intersectionRatio?: number;
  /**
   * If true, use IntersectionObserver with a rootMargin that effectively
   * triggers when the sentinel reaches the vertical center of the viewport.
   */
  triggerAtCenter?: boolean;
  status?: OfferStatusTypes;
}

export const ScrollLoadOffers: AppFC<ScrollLoadOffersProps> = ({
  children,
  filterParams,
  intersectionRatio,
  triggerAtCenter,
  status = OfferStatusTypes.Open,
}): AppElement => {
  const { offerState, updateOfferState } = useContext(OfferContext);
  const { loadMoreOffers } = useOffer();

  const { isLoadingMore, currentPage, hasMore } = offerState;

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return; // Stop loading if no more offers available

    updateOfferState({
      type: OfferActionTypes.SetLoadingMore,
      payload: true,
    });

    try {
      const params = {
        ...filterParams,
        OfferStatus: status,
      };

      await loadMoreOffers(params, currentPage + 1);
    } catch (error) {
      console.error('Error loading more offers:', error);
      updateOfferState({
        type: OfferActionTypes.SetLoadingMore,
        payload: false,
      });
    }
  }, [isLoadingMore, hasMore, currentPage, filterParams, loadMoreOffers, updateOfferState]);

  // If an intersectionRatio is provided, use IntersectionObserver on a sentinel
  // element placed after children. Otherwise fall back to existing scroll logic.
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;

    let cancelled = false;

    // If triggerAtCenter is true, use rootMargin to offset trigger to viewport center.
    if (triggerAtCenter) {
      const observer = new IntersectionObserver((entries) => {
        if (cancelled) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoadingMore && hasMore) {
            handleLoadMore();
          }
        });
      }, { root: null, rootMargin: '-50% 0px -50% 0px', threshold: 0 });

      observer.observe(el);

      return () => {
        cancelled = true;
        observer.disconnect();
      };
    }

    // Otherwise, fall back to intersectionRatio if provided
    if (typeof intersectionRatio === 'number') {
      const observer = new IntersectionObserver((entries) => {
        if (cancelled) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoadingMore && hasMore) {
            handleLoadMore();
          }
        });
      }, { threshold: intersectionRatio });

      observer.observe(el);

      return () => {
        cancelled = true;
        observer.disconnect();
      };
    }

    return undefined;
  }, [intersectionRatio, handleLoadMore, isLoadingMore, hasMore, triggerAtCenter]);

  // Fallback: existing scroll listener when intersectionRatio is not provided
  useScrollLoad({
    isLoading: isLoadingMore || !hasMore,
    onLoadMore: handleLoadMore,
    threshold: 10, // Load when 10px from bottom (unused when using intersection observer)
  });

  return (
    <div className="w-full">
      {children}
      {/* sentinel for intersection observer; height kept small and invisible */}
      {(typeof intersectionRatio === 'number' || triggerAtCenter) && <div ref={sentinelRef} style={{ width: '100%', height: 1 }} />}
    </div>
  );
};
